/**
 * CountingScreen - Product counting screen for mobile emulator
 */
import React from 'react';
import { ChevronLeft, Scan, RefreshCw, Package, Trash2 } from 'lucide-react';
import type { Hospital } from '../../services/hospitalService';
import type { Location } from '../../services/mobileService';
import type { ItemContado } from '../../hooks/useMobileScanner';
import { ScannerModal } from './ScannerModal';

interface CountingScreenProps {
  selectedHospital: Hospital | null;
  selectedLocal: Location | null;
  isOnline: boolean;
  isSyncing: boolean;
  itensContados: ItemContado[];
  scannerAtivo: boolean;
  codigoBarras: string;
  ultimaLeitura: string;
  feedbackLeitura: boolean;
  itensEscaneados: ItemContado[];
  scannerError: string;
  codigoBarrasInputRef: React.RefObject<HTMLInputElement>;
  onBack: () => void;
  onOpenScanner: () => void;
  onCloseScanner: () => void;
  onFinalizar: () => void;
  onLimparItens: () => void;
  onRemoverItem: (id: string) => void;
  onCodigoBarrasChange: (value: string) => void;
  onBuscarProduto: (codigo: string) => void;
  onSimularLeitura: () => void;
  onEditarQuantidadeEscaneado: (id: string, quantidade: number) => void;
  onRemoverItemEscaneado: (id: string) => void;
  onConfirmarItensEscaneados: () => void;
}

export const CountingScreen: React.FC<CountingScreenProps> = ({
  selectedHospital,
  selectedLocal,
  isOnline,
  isSyncing,
  itensContados,
  scannerAtivo,
  codigoBarras,
  ultimaLeitura,
  feedbackLeitura,
  itensEscaneados,
  scannerError,
  codigoBarrasInputRef,
  onBack,
  onOpenScanner,
  onCloseScanner,
  onFinalizar,
  onLimparItens,
  onRemoverItem,
  onCodigoBarrasChange,
  onBuscarProduto,
  onSimularLeitura,
  onEditarQuantidadeEscaneado,
  onRemoverItemEscaneado,
  onConfirmarItensEscaneados,
}) => {
  return (
    <div className="flex-1 flex flex-col">
      {/* Header with breadcrumb */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white/80 hover:text-white mb-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </button>
        <p className="text-sm opacity-90">{selectedHospital?.nome}</p>
        <p className="font-bold">{selectedLocal?.nome}</p>
      </div>

      {/* Action buttons */}
      <div className="p-4 grid grid-cols-2 gap-3">
        <button
          onClick={onOpenScanner}
          className="bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-lg font-semibold flex flex-col items-center gap-2"
        >
          <Scan className="w-6 h-6" />
          Escanear
        </button>
        <button
          onClick={onFinalizar}
          disabled={!isOnline || itensContados.length === 0 || isSyncing}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-4 rounded-lg font-semibold flex flex-col items-center gap-2"
        >
          {isSyncing ? (
            <>
              <RefreshCw className="w-6 h-6 animate-spin" />
              Enviando...
            </>
          ) : (
            <>
              <RefreshCw className="w-6 h-6" />
              Finalizar
            </>
          )}
        </button>
      </div>

      {/* Scanner Modal */}
      {scannerAtivo && (
        <ScannerModal
          codigoBarras={codigoBarras}
          ultimaLeitura={ultimaLeitura}
          feedbackLeitura={feedbackLeitura}
          itensEscaneados={itensEscaneados}
          error={scannerError}
          codigoBarrasInputRef={codigoBarrasInputRef}
          onCodigoBarrasChange={onCodigoBarrasChange}
          onBuscarProduto={onBuscarProduto}
          onSimularLeitura={onSimularLeitura}
          onEditarQuantidade={onEditarQuantidadeEscaneado}
          onRemoverItem={onRemoverItemEscaneado}
          onConfirmarItens={onConfirmarItensEscaneados}
          onClose={onCloseScanner}
        />
      )}

      {/* Items list */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-gray-900">Itens ({itensContados.length})</h3>
          {itensContados.length > 0 && (
            <button
              onClick={onLimparItens}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Limpar
            </button>
          )}
        </div>

        {itensContados.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">Nenhum item contado</p>
            <p className="text-sm text-gray-400">Escaneie produtos para começar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {itensContados.map((item) => (
              <div
                key={item.id}
                className="bg-white border-2 border-gray-200 rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">{item.nome}</p>
                    <p className="text-xs text-gray-500 font-mono">{item.codigo}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.unidade}</p>
                  </div>
                  <button
                    onClick={() => onRemoverItem(item.id)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                  <span className="text-2xl font-bold text-blue-600">{item.quantidade}</span>
                  <span className="text-xs text-gray-500">{item.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Total: <span className="font-bold">{itensContados.reduce((acc, item) => acc + item.quantidade, 0)} unidades</span>
          </p>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-xs text-gray-600">{isOnline ? 'Online' : 'Offline'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
